import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Settings } from "lucide-react";

import { useAuth } from "@/lib/AuthProvider";
import { useCompany } from "@/lib/CompanyProvider";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useSettingsData } from "@/hooks/settings/useSettingsData";
import { SubscriptionCard } from "@/components/settings/SubscriptionCard";
import { BillingHistory } from "@/components/settings/BillingHistory";
import { SettingsForm } from "@/components/settings/SettingsForm";
import { ReceiptPreview } from "@/components/settings/ReceiptPreview";

export const Route = createFileRoute("/app/settings")({
  component: SettingsModule,
});

function SettingsModule() {
  const { activeCompanyId } = useCompany();
  const { user } = useAuth();
  
  const role = user?.user_metadata?.role || "COLABORADOR";
  const isAdmin = role === "ADMIN" || role === "SUPERADMIN";

  const {
    company,
    subscription,
    billingInfo,
    loading,
    loadingBilling,
    formData,
    setFormData,
    isEditing,
    setIsEditing,
    errorMsg,
    setErrorMsg,
    successMsg,
    isUpgradeModalOpen,
    setIsUpgradeModalOpen,
    updateSettings,
    handleChange,
    resetForm,
  } = useSettingsData(activeCompanyId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      setErrorMsg("O nome da empresa é obrigatório.");
      return;
    }
    updateSettings.mutate(formData);
  };

  if (loading) {
    return (
      <div className="p-8 text-white flex justify-center items-center h-64">
        Carregando configurações...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="text-blue-500" size={32} />
            Configurações do Lava-Jato
          </h1>
          <p className="text-slate-400 mt-1">
            Personalize os dados da sua empresa, recibos e contato.
          </p>
        </div>
      </div>

      {errorMsg && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl flex items-center gap-2">
          <strong>Erro:</strong> {errorMsg}
        </div>
      )}

      {successMsg && (
        <div className="bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 p-4 rounded-xl flex items-center gap-2">
          <strong>Sucesso:</strong> {successMsg}
        </div>
      )}

      <SubscriptionCard
        subscription={subscription}
        setIsUpgradeModalOpen={setIsUpgradeModalOpen}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SettingsForm
            formData={formData}
            setFormData={setFormData}
            handleChange={handleChange}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            updateSettings={updateSettings}
            handleSubmit={handleSubmit}
            resetForm={resetForm}
            isAdmin={isAdmin}
          />

          <BillingHistory
            billingInfo={billingInfo}
            loadingBilling={loadingBilling}
          />
        </div>

        <ReceiptPreview formData={formData} />
      </div>

      {company?.id && (
        <UpgradeModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          companyId={company.id}
        />
      )}
    </div>
  );
}
